import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const initialForm = {
  full_name: '',
  address: '',
  contact_number: '',
  whatsapp_number: '',
  email_address: '',
  age: '',
  gender: '',
  institution_name: '',
  institution_address: '',
  course: '',
  year_semester: '',
  desired_industry: '',
  desired_position: '',
  desired_location: '',
  preferred_start_date: '',
  preferred_end_date: '',
  availability: '',
};

const INDUSTRIES = [
  'Information Technology', 'Software Development', 'Data Science & Analytics',
  'Marketing & Advertising', 'Finance & Banking', 'Human Resources',
  'Healthcare & Pharma', 'Education & Training', 'Civil & Construction',
  'Mechanical Engineering', 'Electrical Engineering', 'Retail & E-commerce',
  'Logistics & Supply Chain', 'Media & Journalism', 'Hospitality & Tourism',
  'Legal & Compliance', 'Research & Development', 'Non-Profit & Social Work',
  'Government & Public Sector', 'Architecture & Design', 'Others',
];

const QUOTES = [
  { text: 'An internship is worth more than a thousand lectures.', author: 'Industry Wisdom' },
  { text: 'Your internship is the bridge between education and your career.', author: 'Career Counselor' },
  { text: 'Every expert was once a beginner. Take the first step.', author: 'Motivational' },
];

const inputCls = 'mt-1.5 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100 transition';
const labelCls = 'block text-xs font-bold text-gray-700 uppercase tracking-wide';
const sectionTitleCls = 'mb-4 flex items-center gap-2 text-base font-black uppercase tracking-widest text-blue-700';

const Internship = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [quoteIdx] = useState(() => Math.floor(Math.random() * QUOTES.length));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contact_number' || name === 'whatsapp_number') {
      setForm(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 10) }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{10}$/.test(form.contact_number)) {
      setError('Contact number must be exactly 10 digits.');
      return;
    }
    if (form.whatsapp_number && !/^\d{10}$/.test(form.whatsapp_number)) {
      setError('WhatsApp number must be exactly 10 digits.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/internship`, form);
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 font-official">

      {/* ── Hero Banner ───────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#1d4ed8] text-white">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -right-16 h-72 w-72 rounded-full bg-indigo-400/25 blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/5 blur-2xl" />

        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">

            {/* Left — copy */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-400/10 px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.3em] text-blue-300">
                Dani Job Club &nbsp;·&nbsp; Internship Program
              </span>
              <h1 className="mt-6 text-4xl font-black leading-tight md:text-5xl lg:text-6xl">
                Launch Your <span className="text-yellow-400">Career</span> with a Real Internship
              </h1>
              <p className="mt-5 text-lg leading-8 text-blue-100/90">
                Gain hands-on experience, build your professional network, and turn your education into a career head-start. Apply for an internship placement today.
              </p>

              {/* Stats row */}
              <div className="mt-8 flex flex-wrap gap-4">
                {[
                  { label: 'Industries', value: '20+' },
                  { label: 'Partner Companies', value: '100+' },
                  { label: 'Placements', value: '500+' },
                ].map(s => (
                  <div key={s.label} className="rounded-2xl border border-white/10 bg-white/10 px-5 py-3 backdrop-blur-sm">
                    <p className="text-2xl font-black text-yellow-400">{s.value}</p>
                    <p className="text-xs font-bold text-blue-200">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="mt-8 rounded-2xl border-l-4 border-yellow-400 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-base font-semibold italic text-white/90">"{QUOTES[quoteIdx].text}"</p>
                <footer className="mt-2 text-xs font-bold text-yellow-300">— {QUOTES[quoteIdx].author}</footer>
              </blockquote>
            </div>

            {/* Right — illustration */}
            <div className="flex items-center justify-center">
              <div className="relative w-full max-w-sm">
                {/* Card stack decoration */}
                <div className="absolute -top-4 -left-4 h-full w-full rounded-[32px] border border-blue-400/20 bg-white/5" />
                <div className="absolute -top-2 -left-2 h-full w-full rounded-[32px] border border-blue-400/15 bg-white/5" />
                <div className="relative rounded-[32px] border border-white/15 bg-white/10 p-8 backdrop-blur-md">
                  <div className="space-y-4">
                    {[
                      { icon: '🎓', title: 'Learn from professionals', desc: 'Real projects with industry mentors' },
                      { icon: '💼', title: 'Build your portfolio', desc: 'Work on meaningful, real-world tasks' },
                      { icon: '🤝', title: 'Grow your network', desc: 'Connect with companies & leaders' },
                      { icon: '🚀', title: 'Launch your career', desc: 'Many interns get job offers' },
                    ].map(item => (
                      <div key={item.title} className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-yellow-400/20 text-xl">
                          {item.icon}
                        </span>
                        <div>
                          <p className="text-sm font-bold text-white">{item.title}</p>
                          <p className="text-xs text-blue-200">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 40 C360 0 1080 0 1440 40 L1440 40 L0 40Z" fill="#f0f9ff" />
          </svg>
        </div>
      </div>

      {/* ── Quotes strip ─────────────────────────────────────────────────────── */}
      <div className="bg-blue-50/60 px-6 py-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {QUOTES.map((q, i) => (
              <div
                key={i}
                className={`rounded-2xl border px-5 py-4 ${
                  i === quoteIdx
                    ? 'border-blue-300 bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'border-gray-200 bg-white text-gray-800'
                }`}
              >
                <p className={`text-sm font-semibold italic leading-6 ${i === quoteIdx ? 'text-white' : 'text-gray-700'}`}>
                  "{q.text}"
                </p>
                <p className={`mt-2 text-xs font-black uppercase tracking-widest ${i === quoteIdx ? 'text-yellow-300' : 'text-blue-500'}`}>
                  — {q.author}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Form ─────────────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-4xl px-4 py-12 md:px-6">
        <div className="mb-8 text-center">
          <span className="text-xs font-extrabold uppercase tracking-[0.3em] text-blue-500">Apply Now</span>
          <h2 className="mt-2 text-3xl font-black text-gray-900 md:text-4xl">Internship Application Form</h2>
          <p className="mt-3 text-gray-500">Fill in your details below and we'll match you with the right internship opportunity.</p>
        </div>

        {success ? (
          <div className="rounded-[28px] border border-emerald-200 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500 text-3xl text-white shadow-lg">✓</div>
            <h3 className="mt-6 text-2xl font-black text-gray-900">Application Submitted!</h3>
            <p className="mt-3 text-gray-600">Thank you for applying. Our team will review your application and get in touch with you shortly.</p>
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="mt-6 rounded-xl bg-blue-600 px-8 py-3 text-sm font-extrabold text-white hover:bg-blue-700 transition"
            >
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm md:p-8 space-y-8">

            {/* ── Personal Information ── */}
            <div>
              <p className={sectionTitleCls}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-black text-blue-700">1</span>
                Personal Information
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>Full Name <span className="text-red-500">*</span></span>
                  <input name="full_name" value={form.full_name} onChange={handleChange}
                    placeholder="Enter your full name" required className={inputCls} />
                </label>
                <label className="block md:col-span-2">
                  <span className={labelCls}>Address</span>
                  <input name="address" value={form.address} onChange={handleChange}
                    placeholder="Enter your full address" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Contact Number <span className="text-red-500">*</span></span>
                  <input name="contact_number" value={form.contact_number} onChange={handleChange}
                    placeholder="10-digit mobile number" maxLength={10} required className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>WhatsApp Number</span>
                  <input name="whatsapp_number" value={form.whatsapp_number} onChange={handleChange}
                    placeholder="10-digit WhatsApp number" maxLength={10} className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Email Address <span className="text-red-500">*</span></span>
                  <input type="email" name="email_address" value={form.email_address} onChange={handleChange}
                    placeholder="your@email.com" required className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Age</span>
                  <input type="number" name="age" value={form.age} onChange={handleChange}
                    placeholder="Your age" min="16" max="35" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Gender</span>
                  <select name="gender" value={form.gender} onChange={handleChange} className={inputCls}>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* ── Educational Background ── */}
            <div>
              <p className={sectionTitleCls}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-black text-indigo-700">2</span>
                Educational Background
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>College / Institution Name</span>
                  <input name="institution_name" value={form.institution_name} onChange={handleChange}
                    placeholder="Name of your college or institution" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>College / Institution Address</span>
                  <input name="institution_address" value={form.institution_address} onChange={handleChange}
                    placeholder="City, District, State" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Course</span>
                  <input name="course" value={form.course} onChange={handleChange}
                    placeholder="e.g. B.Tech CSE, BBA, MBA..." className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Year / Semester</span>
                  <input name="year_semester" value={form.year_semester} onChange={handleChange}
                    placeholder="e.g. 3rd Year / 6th Semester" className={inputCls} />
                </label>
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* ── Internship Details ── */}
            <div>
              <p className={sectionTitleCls}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-yellow-100 text-xs font-black text-yellow-700">3</span>
                Internship Details
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>Desired Industry</span>
                  <select name="desired_industry" value={form.desired_industry} onChange={handleChange} className={inputCls}>
                    <option value="">Select industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={labelCls}>Desired Position / Role</span>
                  <input name="desired_position" value={form.desired_position} onChange={handleChange}
                    placeholder="e.g. Software Developer Intern" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Desired Location</span>
                  <input name="desired_location" value={form.desired_location} onChange={handleChange}
                    placeholder="City or district where you want to intern" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Preferred Start Date</span>
                  <input type="date" name="preferred_start_date" value={form.preferred_start_date} onChange={handleChange}
                    className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Preferred End Date</span>
                  <input type="date" name="preferred_end_date" value={form.preferred_end_date} onChange={handleChange}
                    className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Availability</span>
                  <select name="availability" value={form.availability} onChange={handleChange} className={inputCls}>
                    <option value="">Select availability</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </label>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-4 border-t border-gray-100 pt-6">
              <p className="text-xs text-gray-400">Fields marked <span className="text-red-500 font-bold">*</span> are required.</p>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-2xl bg-blue-600 px-8 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-blue-200 hover:bg-blue-700 transition disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}
      </div>

    </div>
  );
};

export default Internship;
